import _ from 'lodash';
import { fromBase64 } from '../../../shared/util/base64';
import { isNotNullOrUndefined } from '../../../shared/util/collection/isNotNullOrUndefined';
import { GroupWithComposableDevice } from '../../group/group.model';
import { ComposableDevicesStoreInfoResponse } from '../../store/hooks/useDevicesOperator';
import { ZoneResponse } from '../../store/modules/zone/ZoneService.model';
import {
  AirConnectNode,
  CommanderAirConnectCreateComposableDeviceItemAPI,
} from '../CommanderAirConnectAPI/CommanderAirConnectAPI.model';
import { ComposableDevicesManagerStoreInfoResponse } from '../hooks/useDevicesManager';
import { DevicesStoreInfoResponse } from '../../store/StoreService.model';

// Constants for error messages
const ERROR_MESSAGES = {
  NO_NODE_MAC: {
    type: 'error',
    label: 'initial_setup.connect_commander.no_node_mac',
  },
  NODE_DUPLICATED: {
    type: 'duplicated',
    label: 'initial_setup.connect_commander.node_duplicated',
  },
} as const;

type DeviceInfo =
  | ComposableDevicesStoreInfoResponse
  | ComposableDevicesManagerStoreInfoResponse;

/**
 * Get shelf mappings for a device
 * - For non-composable devices: returns all shelves with settings
 * - For composable devices: returns only shelves mapped in composable zone mapping
 */
export function getShelfMapping(device: DeviceInfo) {
  const validShelfMappings = device.shelfMappings.filter(isNotNullOrUndefined);

  // For non-composable devices, return all shelves that have settings
  if (!device.composableDevice) {
    return validShelfMappings.filter(
      (shelf) => shelf && device.shelfSettings[shelf.id]
    );
  }

  // For composable devices, return only shelves in the composable zone mapping
  const composableZoneIds = Object.values(device.composableZoneMapping ?? {});
  return validShelfMappings.filter((shelf) => {
    if (!shelf) return false;
    return composableZoneIds.some((zoneId) => Number(zoneId) === shelf.id);
  });
}

/**
 * Get shelf settings for a device
 * - For non-composable devices: returns all shelf settings
 * - For composable devices: returns only settings for mapped zones
 */
export function getShelfSettings(
  device: DeviceInfo
): Record<string, ZoneResponse> {
  const defaultSettings = device.shelfSettings || {};

  // For non-composable devices, return all settings
  if (!device.composableDeviceId) {
    return defaultSettings;
  }

  // For composable devices, return only settings for mapped zones
  if (!device.composableZoneMapping) {
    return defaultSettings;
  }

  const mappedSettings: Record<string, ZoneResponse> = {};
  Object.values(device.composableZoneMapping).forEach((zoneId) => {
    if (device.shelfSettings?.[zoneId]) {
      mappedSettings[zoneId] = device.shelfSettings[zoneId];
    }
  });

  return mappedSettings;
}

/**
 * Get list of duplicated devices from the provided device list
 */
export function getDuplicatedDevicesInList(
  devices: DevicesStoreInfoResponse[],
  groupDevices: GroupWithComposableDevice[] = []
) {
  return findDuplicatedDevices(devices, groupDevices, [], false);
}

interface DuplicationError {
  id: string;
  type: string;
  label: string;
}

/**
 * Check if a device's MAC address exists in the provided node list
 */
function isOldNode(
  nodes: AirConnectNode[],
  device:
    | ComposableDevicesStoreInfoResponse
    | CommanderAirConnectCreateComposableDeviceItemAPI
    | null
): boolean {
  if (!device) return false;
  return !nodes.some((node) => node.macAddress === device.mac);
}

/**
 * Check if a device is already marked as duplicated in the error list
 */
function isAlreadyMarkedAsDuplicated(
  errorList: DuplicationError[],
  deviceId: string
): boolean {
  return errorList.some((error) => error.id === deviceId);
}

/**
 * Check if two devices have the same zone mappings
 */
function haveSameZoneMappings(
  device1: ComposableDevicesStoreInfoResponse,
  device2: ComposableDevicesStoreInfoResponse
): boolean {
  const zoneMappings1 = Object.values(device1.composableZoneMapping ?? {});
  const zoneMappings2 = Object.values(device2.composableZoneMapping ?? {});
  return _.isEqual(zoneMappings1, zoneMappings2);
}

/**
 * Check if two groups have the same control units
 */
function haveSameControlUnits(
  group1: GroupWithComposableDevice | undefined,
  group2: GroupWithComposableDevice | undefined
): boolean {
  const controlUnits1 = Object.values(
    group1?.composableDevice.controlUnits ?? {}
  );
  const controlUnits2 = Object.values(
    group2?.composableDevice.controlUnits ?? {}
  );
  return _.isEqual(controlUnits1, controlUnits2);
}

/**
 * Find the group that contains a specific composable device
 */
function findGroupByDeviceId(
  groups: GroupWithComposableDevice[],
  composableDeviceId: string | null | undefined
): GroupWithComposableDevice | undefined {
  if (!composableDeviceId) return undefined;
  return groups.find((group) => fromBase64(group.id) === composableDeviceId);
}

/**
 * Add duplication error to the list if not already present
 */
function addDuplicationError(
  errorList: DuplicationError[],
  deviceId: string,
  errorType: (typeof ERROR_MESSAGES)[keyof typeof ERROR_MESSAGES]
): void {
  if (!isAlreadyMarkedAsDuplicated(errorList, deviceId)) {
    errorList.push({
      id: deviceId,
      type: errorType.type,
      label: errorType.label,
    });
  }
}

/**
 * Handle marking devices that have no zone mappings or control units
 */
function markDeviceWithNoConfiguration(
  device: ComposableDevicesStoreInfoResponse,
  groups: GroupWithComposableDevice[],
  errorList: DuplicationError[]
): void {
  const hasNoZoneMappings =
    Object.keys(device.composableZoneMapping ?? {}).length === 0;
  const group = findGroupByDeviceId(groups, device.composableDeviceId);
  const hasNoControlUnits = group?.composableDevice.controlUnits.length === 0;

  if (hasNoZoneMappings && hasNoControlUnits) {
    addDuplicationError(errorList, device.uuid, ERROR_MESSAGES.NO_NODE_MAC);
  }
}

/**
 * Handle marking devices with identical configurations as duplicates
 */
function markIdenticalDevicesAsDuplicated(
  device: ComposableDevicesStoreInfoResponse,
  duplicateDevice: ComposableDevicesStoreInfoResponse,
  groups: GroupWithComposableDevice[],
  errorList: DuplicationError[]
): void {
  const sameZoneMappings = haveSameZoneMappings(device, duplicateDevice);
  const group1 = findGroupByDeviceId(groups, device.composableDeviceId);
  const group2 = findGroupByDeviceId(
    groups,
    duplicateDevice.composableDeviceId
  );
  const sameControlUnits = haveSameControlUnits(group1, group2);

  if (sameZoneMappings && sameControlUnits) {
    addDuplicationError(errorList, device.uuid, ERROR_MESSAGES.NODE_DUPLICATED);
    addDuplicationError(
      errorList,
      duplicateDevice.uuid,
      ERROR_MESSAGES.NODE_DUPLICATED
    );
  }
}

/**
 * Validate and mark devices with missing or duplicate configurations
 *
 * This function checks for:
 * - Old nodes (devices not in the node list)
 * - Duplicate MAC addresses with identical configurations
 * - Devices without zone mappings or control units
 */
export const findDuplicatedDevices = (
  flexeServeList: ComposableDevicesStoreInfoResponse[],
  groups: GroupWithComposableDevice[] = [],
  nodes: AirConnectNode[] = [],
  reviewOldNodes = true
): DuplicationError[] => {
  const itemsDuplicated: DuplicationError[] = [];

  flexeServeList.forEach((device, index) => {
    if (flexeServeList.length === 1) {
      return;
    }
    // Mark device if it has no configuration (no zone mappings and no control units)
    markDeviceWithNoConfiguration(device, groups, itemsDuplicated);
    // Check if device is an old node not in the current node list
    if (reviewOldNodes && isOldNode(nodes, device)) {
      addDuplicationError(
        itemsDuplicated,
        device.uuid,
        ERROR_MESSAGES.NO_NODE_MAC
      );
      return;
    }

    // Find duplicate device with same MAC address
    const duplicateDevice = flexeServeList.find(
      (otherDevice, otherIndex) =>
        otherIndex !== index && device.mac === otherDevice.mac
    );
    // Mark device if it has no zone mappings
    if (!Object.values(device.composableZoneMapping ?? {}).length) {
      addDuplicationError(
        itemsDuplicated,
        device.uuid,
        ERROR_MESSAGES.NO_NODE_MAC
      );
    }
    if (duplicateDevice) {
      // Mark devices with identical configurations as duplicates
      markIdenticalDevicesAsDuplicated(
        device,
        duplicateDevice,
        groups,
        itemsDuplicated
      );
    }

    // Add the duplicate device to the error list if not already added
    if (duplicateDevice) {
      addDuplicationError(
        itemsDuplicated,
        duplicateDevice.uuid,
        ERROR_MESSAGES.NODE_DUPLICATED
      );
    }
  });

  return itemsDuplicated;
};
